import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import LabelWithHoverEffect from '@project/common/components/LabelWithHoverEffect';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import type {
    AsbplayerSettings,
    SubtitlesWidthUnit,
    TextSubtitleSettings,
    CustomStyle,
} from '@project/common/settings';
import {
    changeForTextSubtitleSetting,
    maxSubtitlesWidth,
    subtitlesWidthUnits,
    textSubtitleSettingsAreDirty,
    textSubtitleSettingsForTrack,
} from '@project/common/settings';
import { isNumeric } from '@project/common/util';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import RadioGroup from '@project/common/components/SettingsRadioGroup';
import Tooltip from '@project/common/components/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import SubtitleAppearanceTrackSelector from '@project/common/components/SubtitleAppearanceTrackSelector';
import SubtitlePreview from '@project/common/components/SubtitlePreview';
import Stack from '@mui/material/Stack';
import ResponsiveSettingsStack from '@project/common/components/ResponsiveSettingsStack';
import SettingsTextField from '@project/common/components/SettingsTextField';
import NumericSettingInput from '@project/common/components/NumericSettingInput';
import SwitchLabelWithHoverEffect from '@project/common/components/SwitchLabelWithHoverEffect';
import SettingsSection from '@project/common/components/SettingsSection';
import KeyboardShortcutLink from '@project/common/components/KeyboardShortcutLink';

// Filter out keys that look like '0', '1', ... as those are invalid
const cssStyles = Object.keys(document.body.style).filter((s) => !isNumeric(s));

interface AddCustomStyleProps {
    styleKey: string;
    onStyleKey: (styleKey: string) => void;
    onAddCustomStyle: (styleKey: string) => void;
}

function AddCustomStyle({ styleKey, onStyleKey, onAddCustomStyle }: AddCustomStyleProps) {
    const { t } = useTranslation();
    return (
        <Autocomplete
            options={cssStyles}
            value={styleKey}
            fullWidth
            disableClearable
            clearOnEscape
            clearOnBlur
            forcePopupIcon={false}
            onReset={() => onStyleKey('')}
            onChange={(event, newValue) => {
                onStyleKey(newValue ?? '');
            }}
            renderInput={(params) => (
                <SettingsTextField
                    placeholder={t('settings.styleKey')}
                    label={t('settings.addCustomCss')}
                    color="primary"
                    {...params}
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    disabled={cssStyles.find((s) => s === styleKey) === undefined}
                                    onClick={() => {
                                        onAddCustomStyle(styleKey);
                                        onStyleKey(cssStyles[0]);
                                    }}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            )}
        />
    );
}

interface CustomStyleSettingProps {
    customStyle: CustomStyle;
    onCustomStyle: (style: CustomStyle) => void;
    onDelete: () => void;
}

function CustomStyleSetting({ customStyle, onCustomStyle, onDelete }: CustomStyleSettingProps) {
    const { t } = useTranslation();

    return (
        <SettingsTextField
            size="small"
            color="primary"
            label={t('settings.customCssField', { styleKey: customStyle.key })}
            placeholder={t('settings.styleValue')}
            value={customStyle.value}
            onChange={(e) => onCustomStyle({ key: customStyle.key, value: e.target.value })}
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={onDelete}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                },
            }}
        />
    );
}

interface SubtitlesWidthUnitSelectProps {
    value: SubtitlesWidthUnit;
    onValueChange: (value: SubtitlesWidthUnit) => void;
}

function SubtitlesWidthUnitSelect({ value, onValueChange }: SubtitlesWidthUnitSelectProps) {
    const { t } = useTranslation();

    return (
        <Select
            variant="standard"
            disableUnderline
            value={value}
            SelectDisplayProps={{ 'aria-label': t('settings.subtitlesWidth') }}
            onChange={(event) => onValueChange(event.target.value as SubtitlesWidthUnit)}
            sx={{
                fontSize: 'inherit',
                '& .MuiSelect-select': {
                    padding: 0,
                    paddingRight: 2,
                },
                '& .MuiSelect-icon': {
                    right: 0,
                    fontSize: 'inherit',
                },
            }}
        >
            {subtitlesWidthUnits.map((unit) => (
                <MenuItem key={unit} value={unit}>
                    {unit}
                </MenuItem>
            ))}
        </Select>
    );
}

interface Props {
    insideApp?: boolean;
    settings: AsbplayerSettings;
    onSettingChanged: <K extends keyof AsbplayerSettings>(key: K, value: AsbplayerSettings[K]) => Promise<void>;
    onSettingsChanged: (settings: Partial<AsbplayerSettings>) => void;
    extensionInstalled?: boolean;
    extensionSupportsTrackSpecificSettings?: boolean;
    extensionSupportsSubtitlesWidthSetting?: boolean;
    extensionSupportsSubtitlesWidthInPixels?: boolean;
    localFontsAvailable: boolean;
    localFontsPermission?: PermissionState;
    localFontFamilies: string[];
    onUnlockLocalFonts: () => void;
    onViewKeyboardShortcuts: () => void;
}

const SubtitleAppearanceSettingsTab: React.FC<Props> = ({
    insideApp,
    settings,
    onSettingChanged,
    onSettingsChanged,
    extensionInstalled,
    extensionSupportsTrackSpecificSettings,
    extensionSupportsSubtitlesWidthSetting,
    extensionSupportsSubtitlesWidthInPixels,
    localFontsAvailable,
    localFontsPermission,
    localFontFamilies,
    onUnlockLocalFonts,
    onViewKeyboardShortcuts,
}) => {
    const { t } = useTranslation();
    const {
        subtitlePreview,
        imageBasedSubtitleScaleFactor,
        subtitlePositionOffset,
        topSubtitlePositionOffset,
        subtitlesWidth,
        subtitlesWidthUnit,
    } = settings;
    const supportsSubtitlesWidthInPixels = !extensionInstalled || extensionSupportsSubtitlesWidthInPixels;
    const subtitlesWidthUnitForExtension: SubtitlesWidthUnit = supportsSubtitlesWidthInPixels
        ? subtitlesWidthUnit
        : '%';
    const [currentStyleKey, setCurrentStyleKey] = useState<string>(cssStyles[0]);
    const [selectedSubtitleAppearanceTrack, setSelectedSubtitleAppearanceTrack] = useState<number>();
    const {
        subtitleSize,
        subtitleColor,
        subtitleThickness,
        subtitleOutlineThickness,
        subtitleOutlineColor,
        subtitleShadowThickness,
        subtitleShadowColor,
        subtitleBackgroundColor,
        subtitleBackgroundOpacity,
        subtitleFontFamily,
        subtitleCustomStyles,
        subtitleBlur,
        subtitleAlignment,
    } = textSubtitleSettingsForTrack(settings, selectedSubtitleAppearanceTrack);
    const handleSubtitleTextSettingChanged = useCallback(
        <K extends keyof TextSubtitleSettings>(key: K, value: TextSubtitleSettings[K]) => {
            // See settings.ts for more info about how/why subtitle settings are interpreted
            const diff = changeForTextSubtitleSetting({ [key]: value }, settings, selectedSubtitleAppearanceTrack);
            onSettingsChanged(diff);
        },
        [selectedSubtitleAppearanceTrack, settings, onSettingsChanged]
    );

    const handleResetSubtitleTrack = useCallback(() => {
        const diff = changeForTextSubtitleSetting(
            textSubtitleSettingsForTrack(settings, 0),
            settings,
            selectedSubtitleAppearanceTrack
        );
        onSettingsChanged(diff);
    }, [settings, selectedSubtitleAppearanceTrack, onSettingsChanged]);

    const selectedSubtitleAppearanceTrackIsDirty =
        selectedSubtitleAppearanceTrack !== undefined &&
        textSubtitleSettingsAreDirty(settings, selectedSubtitleAppearanceTrack);
    return (
        <Stack spacing={1}>
            {insideApp && (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Tooltip title={t('settings.assSubtitleStylesDescription')}>
                        <SwitchLabelWithHoverEffect
                            sx={{ flex: 1 }}
                            label={t('settings.assSubtitleStyles')}
                            labelPlacement="start"
                            control={
                                <Switch
                                    checked={settings.assSubtitleStyles}
                                    onChange={(_, checked) => onSettingChanged('assSubtitleStyles', checked)}
                                />
                            }
                        />
                    </Tooltip>
                    {settings.assSubtitleStyles &&
                        localFontFamilies.length === 0 &&
                        localFontsAvailable &&
                        localFontsPermission === 'prompt' && (
                            <Tooltip title={t('settings.unlockLocalFonts')}>
                                <IconButton aria-label={t('settings.unlockLocalFonts')} onClick={onUnlockLocalFonts}>
                                    <LockIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                </Stack>
            )}
            {(!extensionInstalled || extensionSupportsTrackSpecificSettings) && (
                <>
                    <SubtitleAppearanceTrackSelector
                        track={selectedSubtitleAppearanceTrack === undefined ? 'all' : selectedSubtitleAppearanceTrack}
                        onTrackSelected={(t) => setSelectedSubtitleAppearanceTrack(t === 'all' ? undefined : t)}
                    />
                    {selectedSubtitleAppearanceTrack !== undefined && (
                        <Button
                            startIcon={<UndoIcon />}
                            disabled={!selectedSubtitleAppearanceTrackIsDirty}
                            onClick={handleResetSubtitleTrack}
                            variant="outlined"
                        >
                            {t('settings.reset')}
                        </Button>
                    )}
                </>
            )}
            <SubtitlePreview
                subtitleSettings={settings}
                text={subtitlePreview}
                onTextChanged={(text) => onSettingChanged('subtitlePreview', text)}
            />
            <SettingsSection>{t('settings.subtitleStyling')}</SettingsSection>
            <ResponsiveSettingsStack>
                {' '}
                {subtitleColor !== undefined && (
                    <SettingsTextField
                        size="small"
                        type="color"
                        label={t('settings.subtitleColor')}
                        fullWidth
                        value={subtitleColor}
                        color="primary"
                        onChange={(event) => handleSubtitleTextSettingChanged('subtitleColor', event.target.value)}
                    />
                )}
                {subtitleSize !== undefined && (
                    <NumericSettingInput
                        size="small"
                        label={t('settings.subtitleSize')}
                        fullWidth
                        value={subtitleSize}
                        color="primary"
                        onValueChange={(value) => handleSubtitleTextSettingChanged('subtitleSize', value)}
                        slotProps={{
                            htmlInput: {
                                min: 1,
                                step: 1,
                            },
                        }}
                    />
                )}
            </ResponsiveSettingsStack>{' '}
            {subtitleThickness !== undefined && (
                <>
                    <Typography variant="subtitle2" color="textSecondary">
                        {t('settings.subtitleThickness')}
                    </Typography>
                    <Slider
                        color="primary"
                        value={subtitleThickness}
                        onChange={(event, value) =>
                            handleSubtitleTextSettingChanged('subtitleThickness', value as number)
                        }
                        min={100}
                        max={900}
                        step={100}
                        marks
                        valueLabelDisplay="auto"
                    />
                </>
            )}
            <ResponsiveSettingsStack>
                {' '}
                {subtitleOutlineColor !== undefined && subtitleOutlineThickness !== 0 && (
                    <SettingsTextField
                        size="small"
                        type="color"
                        label={t('settings.subtitleOutlineColor')}
                        fullWidth
                        value={subtitleOutlineColor}
                        color="primary"
                        onChange={(event) =>
                            handleSubtitleTextSettingChanged('subtitleOutlineColor', event.target.value)
                        }
                    />
                )}
                {subtitleOutlineThickness !== undefined && (
                    <NumericSettingInput
                        size="small"
                        label={t('settings.subtitleOutlineThickness')}
                        helperText={t('settings.subtitleOutlineThicknessHelperText')}
                        fullWidth
                        value={subtitleOutlineThickness}
                        onValueChange={(value) => handleSubtitleTextSettingChanged('subtitleOutlineThickness', value)}
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                step: 0.1,
                            },
                        }}
                        color="primary"
                    />
                )}
            </ResponsiveSettingsStack>
            <ResponsiveSettingsStack>
                {' '}
                {subtitleShadowColor !== undefined && subtitleShadowThickness !== 0 && (
                    <SettingsTextField
                        size="small"
                        type="color"
                        label={t('settings.subtitleShadowColor')}
                        fullWidth
                        value={subtitleShadowColor}
                        color="primary"
                        onChange={(event) =>
                            handleSubtitleTextSettingChanged('subtitleShadowColor', event.target.value)
                        }
                    />
                )}
                {subtitleShadowThickness !== undefined && (
                    <NumericSettingInput
                        size="small"
                        label={t('settings.subtitleShadowThickness')}
                        fullWidth
                        value={subtitleShadowThickness}
                        onValueChange={(value) => handleSubtitleTextSettingChanged('subtitleShadowThickness', value)}
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                step: 0.1,
                            },
                        }}
                        color="primary"
                    />
                )}
            </ResponsiveSettingsStack>
            <ResponsiveSettingsStack>
                {' '}
                {subtitleBackgroundColor !== undefined && subtitleBackgroundOpacity !== 0 && (
                    <SettingsTextField
                        size="small"
                        type="color"
                        label={t('settings.subtitleBackgroundColor')}
                        fullWidth
                        value={subtitleBackgroundColor}
                        color="primary"
                        onChange={(event) =>
                            handleSubtitleTextSettingChanged('subtitleBackgroundColor', event.target.value)
                        }
                    />
                )}
                {subtitleBackgroundOpacity !== undefined && (
                    <NumericSettingInput
                        size="small"
                        label={t('settings.subtitleBackgroundOpacity')}
                        fullWidth
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: 1,
                                step: 0.1,
                            },
                        }}
                        value={subtitleBackgroundOpacity}
                        color="primary"
                        onValueChange={(value) => handleSubtitleTextSettingChanged('subtitleBackgroundOpacity', value)}
                    />
                )}
            </ResponsiveSettingsStack>{' '}
            {subtitleFontFamily !== undefined && (
                <FormControl fullWidth>
                    <FormLabel>{t('settings.subtitleFontFamily')}</FormLabel>
                    <SettingsTextField
                        size="small"
                        type="text"
                        select={localFontFamilies.length > 0}
                        // label={t('settings.subtitleFontFamily')}
                        fullWidth
                        value={subtitleFontFamily}
                        color="primary"
                        onChange={(event) => handleSubtitleTextSettingChanged('subtitleFontFamily', event.target.value)}
                        slotProps={{
                            input: {
                                endAdornment:
                                    localFontFamilies.length === 0 &&
                                    localFontsAvailable &&
                                    localFontsPermission === 'prompt' ? (
                                        <Tooltip title={t('settings.unlockLocalFonts')}>
                                            <IconButton onClick={onUnlockLocalFonts}>
                                                <LockIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null,
                            },
                        }}
                    >
                        {localFontFamilies.length > 0
                            ? localFontFamilies.map((f) => (
                                  <MenuItem key={f} value={f}>
                                      {f}
                                  </MenuItem>
                              ))
                            : null}
                    </SettingsTextField>
                </FormControl>
            )}
            {subtitleCustomStyles !== undefined && (
                <>
                    {subtitleCustomStyles.map((customStyle, index) => {
                        return (
                            <CustomStyleSetting
                                key={index}
                                customStyle={customStyle}
                                onCustomStyle={(newCustomStyle: CustomStyle) => {
                                    const newValue = [...settings.subtitleCustomStyles];
                                    newValue[index] = { ...newCustomStyle };
                                    handleSubtitleTextSettingChanged('subtitleCustomStyles', newValue);
                                }}
                                onDelete={() => {
                                    const newValue: CustomStyle[] = [];
                                    for (let j = 0; j < settings.subtitleCustomStyles.length; ++j) {
                                        if (j !== index) {
                                            newValue.push(settings.subtitleCustomStyles[j]);
                                        }
                                    }
                                    handleSubtitleTextSettingChanged('subtitleCustomStyles', newValue);
                                }}
                            />
                        );
                    })}
                    <AddCustomStyle
                        styleKey={currentStyleKey}
                        onStyleKey={setCurrentStyleKey}
                        onAddCustomStyle={(styleKey) =>
                            handleSubtitleTextSettingChanged('subtitleCustomStyles', [
                                ...settings.subtitleCustomStyles,
                                { key: styleKey, value: '' },
                            ])
                        }
                    />
                </>
            )}
            {subtitleBlur !== undefined && (
                <Tooltip placement="bottom-end" title={t('settings.subtitleBlurDescription')}>
                    <SwitchLabelWithHoverEffect
                        control={
                            <Switch
                                checked={subtitleBlur}
                                onChange={(e) => {
                                    handleSubtitleTextSettingChanged('subtitleBlur', e.target.checked);
                                }}
                            />
                        }
                        label={t('settings.subtitleBlur')}
                        labelPlacement="start"
                    />
                </Tooltip>
            )}
            {selectedSubtitleAppearanceTrack === undefined && (
                <NumericSettingInput
                    size="small"
                    label={t('settings.imageBasedSubtitleScaleFactor')}
                    placeholder="Inherited"
                    fullWidth
                    slotProps={{
                        htmlInput: {
                            min: 0,
                            max: 1,
                            step: 0.1,
                        },
                    }}
                    value={imageBasedSubtitleScaleFactor}
                    color="primary"
                    onValueChange={(value) => void onSettingChanged('imageBasedSubtitleScaleFactor', value)}
                />
            )}
            <SettingsSection>{t('settings.layout')}</SettingsSection>
            {subtitleAlignment !== undefined && (
                <FormControl>
                    <FormLabel component="legend">{t('settings.subtitleAlignment')}</FormLabel>
                    <RadioGroup>
                        <LabelWithHoverEffect
                            control={
                                <Radio
                                    checked={subtitleAlignment === 'bottom'}
                                    value={'bottom'}
                                    onChange={(event) =>
                                        event.target.checked &&
                                        handleSubtitleTextSettingChanged('subtitleAlignment', 'bottom')
                                    }
                                />
                            }
                            label={t('settings.subtitleAlignmentBottom')}
                        />
                        <LabelWithHoverEffect
                            control={
                                <Radio
                                    checked={subtitleAlignment === 'top'}
                                    value={'top'}
                                    onChange={(event) =>
                                        event.target.checked &&
                                        handleSubtitleTextSettingChanged('subtitleAlignment', 'top')
                                    }
                                />
                            }
                            label={t('settings.subtitleAlignmentTop')}
                        />
                    </RadioGroup>
                </FormControl>
            )}
            {selectedSubtitleAppearanceTrack === undefined && (
                <>
                    <ResponsiveSettingsStack>
                        {' '}
                        <NumericSettingInput
                            size="small"
                            color="primary"
                            fullWidth
                            label={
                                <>
                                    {t('settings.subtitlePositionOffset')}
                                    <KeyboardShortcutLink
                                        onClick={onViewKeyboardShortcuts}
                                        preset="numericalInputLabel"
                                    />
                                </>
                            }
                            value={subtitlePositionOffset}
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    step: 1,
                                },
                            }}
                            onValueChange={(value) => void onSettingChanged('subtitlePositionOffset', value)}
                        />
                        <NumericSettingInput
                            size="small"
                            color="primary"
                            fullWidth
                            label={
                                <>
                                    {t('settings.topSubtitlePositionOffset')}
                                    <KeyboardShortcutLink
                                        onClick={onViewKeyboardShortcuts}
                                        preset="numericalInputLabel"
                                    />
                                </>
                            }
                            value={topSubtitlePositionOffset}
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    step: 1,
                                },
                            }}
                            onValueChange={(value) => void onSettingChanged('topSubtitlePositionOffset', value)}
                        />
                    </ResponsiveSettingsStack>{' '}
                    {(!extensionInstalled || extensionSupportsSubtitlesWidthSetting) &&
                        (subtitlesWidth === -1 ? (
                            <SettingsTextField
                                size="small"
                                color="primary"
                                fullWidth
                                label={t('settings.subtitlesWidth')}
                                disabled
                                value="auto"
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => void onSettingChanged('subtitlesWidth', 100)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        ) : (
                            <NumericSettingInput
                                size="small"
                                color="primary"
                                fullWidth
                                label={t('settings.subtitlesWidth')}
                                value={subtitlesWidth}
                                normalizeValue={(value) =>
                                    value >= 0 &&
                                    value <= maxSubtitlesWidth(subtitlesWidthUnitForExtension) &&
                                    Number.isFinite(value)
                                        ? value
                                        : undefined
                                }
                                onValueChange={(value) => void onSettingChanged('subtitlesWidth', value)}
                                slotProps={{
                                    htmlInput: {
                                        min: 0,
                                        max: maxSubtitlesWidth(subtitlesWidthUnitForExtension),
                                    },
                                    input: {
                                        endAdornment: (
                                            <>
                                                {supportsSubtitlesWidthInPixels ? (
                                                    <InputAdornment position="end">
                                                        <SubtitlesWidthUnitSelect
                                                            value={subtitlesWidthUnit}
                                                            onValueChange={(unit) =>
                                                                onSettingsChanged({
                                                                    subtitlesWidthUnit: unit,
                                                                    subtitlesWidth: Math.min(
                                                                        subtitlesWidth,
                                                                        maxSubtitlesWidth(unit)
                                                                    ),
                                                                })
                                                            }
                                                        />
                                                    </InputAdornment>
                                                ) : (
                                                    <InputAdornment position="end">%</InputAdornment>
                                                )}
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => void onSettingChanged('subtitlesWidth', -1)}
                                                    >
                                                        <ClearIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            </>
                                        ),
                                    },
                                }}
                            />
                        ))}
                </>
            )}
        </Stack>
    );
};

export default SubtitleAppearanceSettingsTab;
